import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BlogDetail = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${id}`);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);

  const getTitle = (p) => (i18n.language === 'tr' ? p.title_tr : p.title_en);
  const getContent = (p) => (i18n.language === 'tr' ? p.content_tr : p.content_en);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div data-testid="blog-detail-loading" className="min-h-screen bg-[#0F1A2E] pt-40">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-[#1B2B4B] rounded w-1/4" />
            <div className="h-12 bg-[#1B2B4B] rounded w-3/4" />
            <div className="aspect-[16/9] bg-[#1B2B4B] rounded" />
            <div className="space-y-4">
              <div className="h-4 bg-[#1B2B4B] rounded" />
              <div className="h-4 bg-[#1B2B4B] rounded" />
              <div className="h-4 bg-[#1B2B4B] rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div data-testid="blog-detail-not-found" className="min-h-screen bg-[#0F1A2E] pt-40">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-4xl text-white mb-6">Post Not Found</h1>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#C9A84C] hover:gap-3 transition-all"
          >
            <ArrowLeft size={20} />
            {t('blog.backToBlog')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-detail-page" className="overflow-hidden">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Hero Section */}
      <section className="relative pt-40 pb-12 bg-[#0F1A2E]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/blog"
              data-testid="back-to-blog"
              className="inline-flex items-center gap-2 text-[#C9A84C] hover:gap-3 transition-all mb-8"
            >
              <ArrowLeft size={20} />
              {t('blog.backToBlog')}
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2 text-white/40 text-sm">
                <Calendar size={14} />
                {formatDate(post.created_at)}
              </div>
              <div className="flex items-center gap-2 text-[#C9A84C] text-sm">
                <Tag size={14} />
                {post.category}
              </div>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              {getTitle(post)}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      {post.image_url && (
        <section className="pb-12 bg-[#0F1A2E]">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src={post.image_url}
                alt={getTitle(post)}
                className="w-full aspect-[16/9] object-cover"
              />
            </motion.div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-12 md:py-20 bg-[#0F1A2E]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            data-testid="blog-content"
            className="prose prose-lg prose-invert max-w-none"
          >
            <div
              className="text-white/70 leading-relaxed space-y-6 text-lg"
              dangerouslySetInnerHTML={{ __html: getContent(post).replace(/\n/g, '<br />') }}
            />
          </motion.article>
        </div>
      </section>

      {/* Back to Blog CTA */}
      <section className="py-16 bg-[#0A1423]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Link
            to="/blog"
            data-testid="back-to-blog-cta"
            className="group inline-flex items-center gap-3 border border-[#C9A84C] text-[#C9A84C] px-8 py-4 font-semibold tracking-wide hover:bg-[#C9A84C] hover:text-[#0F1A2E] transition-all duration-300"
          >
            <ArrowLeft size={20} />
            {t('blog.backToBlog')}
          </Link>
        </div>
      </section>
    </div>
  );
};
